import { Icon } from '@iconify/react';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import dayjs from 'dayjs';
import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { tasksApi } from '../tasks.api';
import type { Assignment, DailyAllocation } from '../tasks.types';
import { errorMessage, fmtHours, isoDate, num, today } from '../tasks.utils';
import DialogShell from './DialogShell';
import { useCapacityConfirm, wasCancelled } from './useCapacityConfirm';

/** Edit the date-wise plan (daily allocation) of the current assignment. */
export default function PlanDialog({
  open,
  assignment,
  rows,
  onClose,
  onDone,
}: {
  open: boolean;
  assignment: Assignment;
  rows: DailyAllocation[];
  onClose: () => void;
  onDone: () => void;
}) {
  const [days, setDays] = useState<{ workDate: string; hours: string }[]>([]);
  const [busy, setBusy] = useState(false);
  const { run, dialog } = useCapacityConfirm();

  useEffect(() => {
    if (!open) return;
    const mine = rows.filter((r) => r.assignmentId === assignment.id && r.active);
    setDays(mine.length ? mine.map((r) => ({ workDate: r.workDate, hours: String(num(r.allocatedHours)) })) : [{ workDate: today(), hours: '' }]);
  }, [open, rows, assignment.id]);

  const total = days.reduce((s, d) => s + (Number(d.hours) || 0), 0);
  const over = total > num(assignment.allocatedHours);

  const update = (i: number, k: 'workDate' | 'hours', v: string) => setDays((ds) => ds.map((d, j) => (j === i ? { ...d, [k]: v } : d)));
  const addDay = () => {
    const last = days[days.length - 1]?.workDate;
    setDays((ds) => [...ds, { workDate: last ? isoDate(dayjs(last).add(1, 'day')) : today(), hours: '' }]);
  };

  const submit = async () => {
    const clean = days.filter((d) => d.workDate && d.hours !== '').map((d) => ({ workDate: d.workDate, hours: Number(d.hours) }));
    if (new Set(clean.map((d) => d.workDate)).size !== clean.length) return toast.error('Each date may appear once.');
    setBusy(true);
    try {
      await run((allow) => tasksApi.plan(assignment.id, clean, allow));
      toast.success('Plan saved');
      onDone();
    } catch (e) {
      if (!wasCancelled(e)) toast.error(errorMessage(e, 'Could not save the plan'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <DialogShell open={open} title={`DAILY PLAN · ${assignment.employeeName}`} onClose={onClose} onSubmit={submit} busy={busy} disabled={over}>
        <Typography variant='body2' color={over ? 'error' : 'text.secondary'}>
          {fmtHours(total)} planned of {fmtHours(assignment.allocatedHours)} allocated to this assignment.
        </Typography>
        {days.map((d, i) => (
          <Stack key={i} direction='row' gap={1} alignItems='center'>
            <TextField type='date' size='small' value={d.workDate} onChange={(e) => update(i, 'workDate', e.target.value)} InputLabelProps={{ shrink: true }} label='Date' />
            <TextField type='number' size='small' label='Hours' inputProps={{ min: 0, step: 0.25 }} value={d.hours} onChange={(e) => update(i, 'hours', e.target.value)} />
            <IconButton aria-label='Remove day' onClick={() => setDays((ds) => ds.filter((_, j) => j !== i))}>
              <Icon icon='mdi:close' />
            </IconButton>
          </Stack>
        ))}
        <Button startIcon={<Icon icon='mdi:plus' />} onClick={addDay} sx={{ alignSelf: 'flex-start' }}>
          Add day
        </Button>
      </DialogShell>
      {dialog}
    </>
  );
}
